import React, { useState, useMemo, useEffect } from 'react';
import Image from 'next/image';
import styles from './bookpagehome.module.scss';
import { Fade } from 'react-awesome-reveal';
import { scroller } from 'react-scroll';
import axios from 'axios';
import FreeTrial from './FreeTrial';
import { createPortal } from 'react-dom';
import { Button } from '../Button';
import { Typography } from '../Typography';
import { useRouter } from 'next/router';
import { TfiClose } from 'react-icons/tfi';
import { useLicensingSettings } from '../../utils/licensingService';

const BookPageHome = ({ data }: any) => {
  const [showFreeTrialModal, setShowFreeTrialModal] = useState(false);
  const { data: licensingSettings, isLoading: licensingLoading } = useLicensingSettings();
  const [trialAlreadyRequested, setTrialAlreadyRequested] = useState(false);
  const [trialStatusLoading, setTrialStatusLoading] = useState(true);

  const router = useRouter();

  const heroComponents = data?.components?.find((comp: any) => comp.name === 'hero_section');
  const productoverview = data?.components?.find((comp: any) => comp.name === 'product_overview');

  const handleScroll = () => {
    scroller.scrollTo('more', {
      duration: 50,
      delay: 0,
      smooth: 'easeInOutCubic',
      offset: -105,
    });
  };
  const handleFreeTrialClick = () => {
    if (trialAlreadyRequested) return;
    if (!data?.readyToSell && !data?.hasFreeTrial) {
      handleScroll();
    } else {
      setShowFreeTrialModal(true);
    }
  };

  const goToMyOrders = () => {
    router.push('/members-area?tab=myorders');
  };
  const handleBuyClick = () => {
    scroller.scrollTo('licensing-options', {
      duration: 50,
      delay: 0,
      smooth: 'easeInOutCubic',
      offset: -105,
    });
  };

  // Check whether the user already has a free trial request for this product,
  // so the outer "Request Free Trial" button can be disabled before the modal opens.
  useEffect(() => {
    let mounted = true;
    const checkTrialStatus = async () => {
      const token = localStorage.getItem('auth-token');
      const productId = data?.product?._id;
      if (!token || !productId) {
        setTrialStatusLoading(false);
        return;
      }

      try {
        const response = await axios.post(
          `${process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1}/trial-access-product`,
          { product: productId },
          {
            headers: {
              Authorization: token,
              'Content-Type': 'application/json',
            },
          }
        );
        if (!mounted) return;
        setTrialAlreadyRequested(Boolean(response.data?.data && Object.keys(response.data.data).length));
      } catch (e) {
        // Silently ignore - button just stays enabled and the modal will re-check anyway.
      } finally {
        if (mounted) setTrialStatusLoading(false);
      }
    };
    checkTrialStatus();
    return () => {
      mounted = false;
    };
  }, [data?.product?._id]);

  // Check if any license option is enabled
  const hasAnyLicense = licensingSettings
    ? licensingSettings.showSubscription ||
      licensingSettings.showBinary ||
      licensingSettings.showSourceCode
    : true; // Default to true if settings not loaded yet

  const modalContent = useMemo(() => {
    if (!showFreeTrialModal) return null;

    return createPortal(
      <div className={styles.popupOverlay} onClick={() => setShowFreeTrialModal(false)}>
        <div className={styles.popupModal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.form}>
            <div className={styles.modalPattern}>
              <img src="/modal-background-pattern.svg" alt="Pattern" />
            </div>

            <div className={styles.modalHeader}>
              <div className={styles.modalHeaderIconContainer}>
                <div className={styles.modalHeaderIcon}>
                  <img src="/trial-icon.svg" alt="Trial" />
                </div>
                <div className={styles.close}>
                  <TfiClose
                    onClick={() => setShowFreeTrialModal(false)}
                    style={{
                      color: '#61656C',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  />
                </div>
              </div>
              <div className={styles.modalHeaderText}>
                <Typography variant="text-md" color="grayDark-50" style={{ zIndex: 1 }}>
                  Request Free Trial
                </Typography>
                <Typography variant="text-sm" color="grayDark-400" style={{ zIndex: 1 }}>
                  The trial version allows you to test a single host on a one OS. You can upgrade to
                  the full version to access all hosts and features.
                </Typography>
              </div>
              <FreeTrial
                data={data}
                onClose={() => setShowFreeTrialModal(false)}
                onSuccess={() => setTrialAlreadyRequested(true)}
              />
            </div>
          </div>
        </div>
      </div>,
      document.getElementById('modals') || document.body
    );
  }, [showFreeTrialModal, data]);

  return (
    <>
      <div className={styles.container + ' ' + styles.pattern} id="freetrial">
        <div className={styles.heroSection}>
          <div className={styles.heroContent}>
            <div className={styles.appNameContainer}>
              <Fade triggerOnce duration={1000}>
                <img
                  src={`/${data?.product?.product_name.toLowerCase()}-app-name-lg.svg`}
                  alt={data?.product?.product_name}
                  className={styles.appNameImageLg}
                />
                <img
                  src={`/${data?.product?.product_name.toLowerCase()}-app-name.svg`}
                  alt={data?.product?.product_name}
                  className={styles.appNameImage}
                />
              </Fade>
            </div>
            <Fade triggerOnce duration={1000}>
              <Typography
                variant="display-xl"
                as="h1"
                color="grayDark-50"
                weight="normal"
                className={styles.mainTitle}
              >
                {heroComponents?.mainHeading}
              </Typography>
            </Fade>
            <Fade triggerOnce duration={1000}>
              <Typography
                variant="text-xl"
                as="p"
                color="grayDark-400"
                weight="normal"
                className={styles.description}
              >
                Our Video Plugin Development Framework is a game-changing tool designed for
                developers and video editors alike.
              </Typography>
            </Fade>
            <Fade triggerOnce duration={1000} style={{ width: '100%' }}>
              <div className={styles.heroButtons}>
                <Button
                  onClick={handleScroll}
                  variant="secondary"
                  label="Get started"
                  fullWidth
                  icon={
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="9"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        fill="none"
                      />
                      <path d="M10 8L16 12L10 16V8Z" fill="currentColor" />
                    </svg>
                  }
                  iconPosition="left"
                />
                <Button
                  onClick={() => {
                    router.push('/contact-us');
                  }}
                  label="Chat to us"
                  fullWidth
                />
              </div>
            </Fade>
          </div>
        </div>

        <div className={styles.productOverviewSection} id="more">
          <div className={styles.productOverviewContent}>
            <div className={styles.productOverviewLeft}>
              <div className={styles.productOverviewText}>
                <Fade triggerOnce duration={1000}>
                  <Typography variant="text-md" color="grayDark-300">
                    Our studio
                  </Typography>
                </Fade>
                <Fade triggerOnce duration={1000}>
                  <Typography
                    variant="display-md"
                    color="grayDark-50"
                    weight="semibold"
                    style={{ marginBottom: '36px' }}
                  >
                    Product Overview
                  </Typography>
                </Fade>
                <Fade triggerOnce duration={1000}>
                  <Typography variant="text-lg" color="grayDark-400">
                    Our Video Plugin Development Framework is a game-changing tool designed for
                    developers and video editors alike.
                  </Typography>
                  <Typography variant="text-lg" color="grayDark-400">
                    This framework enables the creation of high-performance video effects and their
                    seamless integration into popular video editing software like DaVinci Resolve
                    and Adobe Premiere Pro.
                  </Typography>
                </Fade>
                <Fade triggerOnce duration={1000}>
                  <Typography
                    variant="text-lg"
                    color="grayDark-400"
                    style={{ marginBottom: '36px' }}
                  >
                    Whether you&apos;re a developer looking to expand your capabilities or an editor
                    seeking new creative options, our framework is a versatile solution for all your
                    plugin development needs.
                  </Typography>
                </Fade>

                {/* Compatible With and Available On Boxes */}
                <Fade triggerOnce duration={1000}>
                  <div className={styles.boxesContainer}>
                    {/* Support Box */}
                    <div className={styles.compatibleWithBox}>
                      <Typography
                        variant="text-sm"
                        color="primary"
                        className={styles.compatibleWithTitle}
                      >
                        Support
                      </Typography>
                      <div className={styles.compatibleWithGrid}>
                        {[...(data?.product?.target_applications || [])].map(
                          (app: string, index: number) => {
                            let iconSrc = '';
                            switch (app) {
                              case 'DaVinci Resolve':
                                iconSrc = '/logo/DaVinci_Resolve_Studio.svg';
                                break;
                              case 'Adobe Premiere Pro':
                                iconSrc = '/logo/Adobe_Premiere_Pro.svg';
                                break;
                              case 'Sony VEGAS Pro':
                                iconSrc = '/logo/Vegas_Pro.svg';
                                break;
                              case 'FinalCut':
                                iconSrc = '/logo/Final_Cut_Pro.svg';
                                break;
                              case 'Natron':
                                iconSrc = '/logo/Natron.svg';
                                break;
                              case 'Nuke':
                                iconSrc = '/logo/Nuke.svg';
                                break;
                              case 'Apple Motion':
                                iconSrc = '/logo/Apple_Motion.svg';
                                break;
                              case 'NVIDIA CUDA':
                                iconSrc = '/logo/NVIDIA_CUDA.svg';
                                break;
                              case 'Apple Metal':
                                iconSrc = '/logo/Apple_Metal.svg';
                                break;
                              case 'OpenCL':
                                iconSrc = '/logo/OpenCL.svg';
                                break;
                              case 'CPU':
                                iconSrc = '/logo/cpu.png';
                                break;
                              default:
                                iconSrc = '';
                            }

                            return iconSrc ? (
                              <div key={index} className={styles.compatibleItem}>
                                <img src={iconSrc} alt={app} />
                              </div>
                            ) : null;
                          }
                        )}
                      </div>
                    </div>

                    {/* Compatible With Box */}
                    <div className={styles.compatibleWithBox}>
                      <Typography
                        variant="text-sm"
                        color="primary"
                        className={styles.compatibleWithTitle}
                      >
                        Compatible With
                      </Typography>
                      <div className={styles.compatibleWithGrid}>
                        {[...(data?.product?.supported_gpu_architectures || [])].map(
                          (app: string, index: number) => {
                            let iconSrc = '';
                            switch (app) {
                              case 'DaVinci Resolve':
                                iconSrc = '/logo/DaVinci_Resolve_Studio.svg';
                                break;
                              case 'Adobe Premiere Pro':
                                iconSrc = '/logo/Adobe_Premiere_Pro.svg';
                                break;
                              case 'Sony VEGAS Pro':
                                iconSrc = '/logo/Vegas_Pro.svg';
                                break;
                              case 'FinalCut':
                                iconSrc = '/logo/Final_Cut_Pro.svg';
                                break;
                              case 'Natron':
                                iconSrc = '/logo/Natron.svg';
                                break;
                              case 'Nuke':
                                iconSrc = '/logo/Nuke.svg';
                                break;
                              case 'Apple Motion':
                                iconSrc = '/logo/Apple_Motion.svg';
                                break;
                              case 'NVIDIA CUDA':
                                iconSrc = '/logo/NVIDIA_CUDA.svg';
                                break;
                              case 'Apple Metal':
                                iconSrc = '/logo/Apple_Metal.svg';
                                break;
                              case 'OpenCL':
                                iconSrc = '/logo/OpenCL.svg';
                                break;
                              case 'CPU':
                                iconSrc = '/logo/cpu.png';
                                break;
                              default:
                                iconSrc = '';
                            }

                            let title = '';
                            switch (app) {
                              case 'NVIDIA CUDA':
                                title = 'NVIDIA';
                                break;
                              case 'Apple Metal':
                                title = 'Apple Metal';
                                break;
                              case 'OpenCL':
                                title = 'Open CL';
                                break;
                              case 'CPU':
                                title = 'CPU';
                                break;
                              default:
                                title = '';
                            }

                            return iconSrc ? (
                              <div key={`platform-${index}`} className={styles.availableOnItem}>
                                <div key={index} className={styles.compatibleItem}>
                                  <img src={iconSrc} alt={app} />
                                </div>
                                <Typography
                                  variant="text-xs"
                                  as="span"
                                  weight="light"
                                  color="grayDark-200"
                                  style={{ textAlign: 'center' }}
                                >
                                  {title}
                                </Typography>
                              </div>
                            ) : null;
                          }
                        )}
                      </div>
                    </div>

                    {/* Available On Box */}
                    <div className={styles.availableOnBox}>
                      <Typography
                        variant="text-sm"
                        color="primary"
                        className={styles.availableOnBoxTitle}
                      >
                        Available On
                      </Typography>
                      <div className={styles.availableOnGrid}>
                        {(data?.product?.supported_platforms || []).map(
                          (platform: string, index: number) => {
                            let iconSrc = '';
                            switch (platform) {
                              case 'Windows':
                                iconSrc = '/logo/Windows.svg';
                                break;
                              case 'MacOS':
                                iconSrc = '/logo/macOS.svg';
                                break;
                              case 'Linux':
                                iconSrc = '/logo/Linux.svg';
                                break;
                              default:
                                iconSrc = '';
                            }

                            let title = '';
                            switch (platform) {
                              case 'Windows':
                                title = 'Windows';
                                break;
                              case 'MacOS':
                                title = 'MacOS';
                                break;
                              case 'Linux':
                                title = 'Linux';
                                break;
                              default:
                                title = '';
                            }

                            return iconSrc ? (
                              <div key={`platform-${index}`} className={styles.availableOnItem}>
                                <div className={styles.availableOnIcon}>
                                  <img src={iconSrc} alt={platform} />
                                </div>
                                <Typography
                                  variant="text-xs"
                                  as="span"
                                  weight="light"
                                  color="grayDark-200"
                                  style={{ textAlign: 'center' }}
                                >
                                  {title}
                                </Typography>
                              </div>
                            ) : null;
                          }
                        )}
                      </div>
                    </div>
                  </div>
                </Fade>

                <Fade triggerOnce duration={1000} style={{ width: '100%' }}>
                  <div className={styles.productButtonsWrapper}>
                    <div className={styles.productButtons}>
                      {!licensingLoading && !hasAnyLicense ? (
                        <>
                          <Button
                            href="/contact-us"
                            label="Get in touch"
                            fullWidth
                            variant="secondary"
                          />
                          {trialStatusLoading ? (
                            <div className={styles.trialButtonSkeleton} style={{ flex: 1 }} />
                          ) : (
                            <Button
                              onClick={handleFreeTrialClick}
                              label={
                                trialAlreadyRequested ? 'Free Trial Already Requested' : 'Request Free Trial'
                              }
                              disabled={trialAlreadyRequested}
                              fullWidth
                            />
                          )}
                        </>
                      ) : (
                        <>
                          {trialStatusLoading ? (
                            <div className={styles.trialButtonSkeleton} style={{ flex: 1 }} />
                          ) : (
                            <Button
                              onClick={handleFreeTrialClick}
                              label={
                                trialAlreadyRequested ? 'Free Trial Already Requested' : 'Request Free Trial'
                              }
                              disabled={trialAlreadyRequested}
                              fullWidth
                              variant="secondary"
                            />
                          )}
                          <Button onClick={handleBuyClick} label="Buy Now" fullWidth />
                        </>
                      )}
                    </div>
                    {!trialStatusLoading && trialAlreadyRequested && (
                      <Typography
                        variant="text-sm"
                        color="grayDark-400"
                        className={styles.trialRequestedHint}
                        onClick={goToMyOrders}
                      >
                        You&apos;ve already requested a trial for this product. Go to{' '}
                        <span className={styles.trialRequestedLink}>My Orders</span> to download it.
                      </Typography>
                    )}
                  </div>
                </Fade>
              </div>
            </div>
            <div className={styles.productImageContainer}>
              <Fade triggerOnce duration={1000} style={{ height: '100%' }}>
                <Image
                  src="/product-image.jpg"
                  alt={productoverview?.mainHeading || 'Product Overview'}
                  layout="fill"
                  objectFit="cover"
                />
              </Fade>
            </div>
          </div>
        </div>
      </div>

      {/* Free Trial Modal */}
      {modalContent}
    </>
  );
};

export default BookPageHome;
